/**
 * Main application component with WorkOS authentication.
 *
 * Features:
 * - Login page at /login (public)
 * - Protected routes require authentication
 * - Agent selection via dropdown (multi-agent mode)
 *
 * Blueprint: specs/021-multi-agent-orchestrator/tasks.md (T023)
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks';
import { ChatPage, LoginPage, OrgSelectPage } from './pages';
import { AuthProvider } from './context/AuthContext';
import { WebSocketProvider, AgentProvider, CxsMcpProvider } from './context';
import './App.css';

function AppRoutes() {
  const { user, isAuthenticated, loading, error, logout } = useAuth();
  const location = useLocation();

  // Multi-org WorkOS: pick organization before JWT exists (public; skip auth spinner)
  if (location.pathname === '/select-org') {
    return <OrgSelectPage />;
  }

  // Show loading while checking session
  if (loading) {
    return (
      <div className="h-screen bg-transparent flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Login page is always accessible
  if (location.pathname === '/login') {
    if (isAuthenticated) {
      return <Navigate to="/chat" replace />;
    }
    return <LoginPage />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show error if agent is unavailable but user is authenticated
  if (error) {
    return (
      <div className="h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="text-blue-500 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AgentProvider>
      <CxsMcpProvider>
        <WebSocketProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/chat/:conversationId?"
              element={<ChatPage user={user!} onLogout={logout} />}
            />
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </WebSocketProvider>
      </CxsMcpProvider>
    </AgentProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
